/** Sequencer pattern presets — predefined note patterns for quick composition.
 *
 * Each pattern preset contains a set of sequencer notes and measure count.
 * Users can load built-in presets or save/load their own patterns.
 */

import type { SequencerNote } from "../components/Sequencer.tsx";

/** A pattern preset definition. */
export type PatternPreset = {
	readonly id: string;
	readonly name: string;
	readonly category: "melodic" | "bass" | "chord" | "arpeggio" | "user";
	readonly description: string;
	readonly notes: readonly SequencerNote[];
	readonly measures: number;
};

/** Storage key for user-saved pattern presets. */
const USER_PATTERNS_KEY = "wasm-synth-pattern-presets" as const;

/** Counter for generating unique note IDs in presets. */
let presetNoteId = 0;

/** Generate a note ID for a preset note. */
function pid(): string {
	presetNoteId += 1;
	return `preset-${presetNoteId}-${Date.now()}`;
}

/** Helper: create a SequencerNote. */
function n(
	note: number,
	startBeat: number,
	durationBeats: number,
	velocity = 100,
): SequencerNote {
	return { id: pid(), note, startBeat, durationBeats, velocity };
}

/** Built-in pattern presets. */
export const BUILT_IN_PATTERNS: readonly PatternPreset[] = [
	{
		id: "pattern-c-major-scale",
		name: "C Major Scale",
		category: "melodic",
		description: "Ascending C major scale over 2 bars.",
		measures: 2,
		notes: [
			n(60, 0, 0.5), // C4
			n(62, 0.5, 0.5), // D4
			n(64, 1, 0.5), // E4
			n(65, 1.5, 0.5), // F4
			n(67, 2, 0.5), // G4
			n(69, 2.5, 0.5), // A4
			n(71, 3, 0.5), // B4
			n(72, 3.5, 0.5), // C5
		],
	},
	{
		id: "pattern-bass-octave",
		name: "Octave Bass",
		category: "bass",
		description: "Simple octave bass pattern over 2 bars.",
		measures: 2,
		notes: [
			n(36, 0, 0.5, 110), // C2
			n(48, 0.5, 0.25, 80), // C3
			n(36, 1, 0.5, 110), // C2
			n(48, 1.5, 0.25, 80), // C3
			n(36, 2, 0.5, 110), // C2
			n(48, 2.5, 0.25, 80), // C3
			n(36, 3, 0.5, 110), // C2
			n(48, 3.5, 0.25, 80), // C3
			n(41, 4, 0.5, 110), // F2
			n(53, 4.5, 0.25, 80), // F3
			n(41, 5, 0.5, 110), // F2
			n(53, 5.5, 0.25, 80), // F3
			n(43, 6, 0.5, 110), // G2
			n(55, 6.5, 0.25, 80), // G3
			n(43, 7, 0.5, 110), // G2
			n(55, 7.5, 0.25, 80), // G3
		],
	},
	{
		id: "pattern-chord-progression",
		name: "Pop Chords",
		category: "chord",
		description: "I-V-vi-IV chord progression in C major.",
		measures: 4,
		notes: [
			// C major (C4, E4, G4)
			n(60, 0, 4),
			n(64, 0, 4),
			n(67, 0, 4),
			// G major (G3, B3, D4)
			n(55, 4, 4),
			n(59, 4, 4),
			n(62, 4, 4),
			// A minor (A3, C4, E4)
			n(57, 8, 4),
			n(60, 8, 4),
			n(64, 8, 4),
			// F major (F3, A3, C4)
			n(53, 12, 4),
			n(57, 12, 4),
			n(60, 12, 4),
		],
	},
	{
		id: "pattern-arpeggio-minor",
		name: "Minor Arpeggio",
		category: "arpeggio",
		description: "Am arpeggio pattern rising and falling.",
		measures: 2,
		notes: [
			n(57, 0, 0.25, 90), // A3
			n(60, 0.25, 0.25, 85), // C4
			n(64, 0.5, 0.25, 80), // E4
			n(69, 0.75, 0.25, 90), // A4
			n(64, 1, 0.25, 80), // E4
			n(60, 1.25, 0.25, 85), // C4
			n(57, 1.5, 0.25, 90), // A3
			n(52, 1.75, 0.25, 95), // E3
			n(57, 2, 0.25, 90), // A3
			n(60, 2.25, 0.25, 85), // C4
			n(64, 2.5, 0.25, 80), // E4
			n(69, 2.75, 0.25, 90), // A4
			n(72, 3, 0.25, 95), // C5
			n(69, 3.25, 0.25, 90), // A4
			n(64, 3.5, 0.25, 85), // E4
			n(60, 3.75, 0.25, 80), // C4
		],
	},
	{
		id: "pattern-four-on-floor",
		name: "Four on the Floor",
		category: "bass",
		description: "Kick-style quarter-note bass pattern.",
		measures: 2,
		notes: [
			n(36, 0, 0.25, 127),
			n(36, 1, 0.25, 127),
			n(36, 2, 0.25, 127),
			n(36, 3, 0.25, 127),
			n(36, 4, 0.25, 127),
			n(36, 5, 0.25, 127),
			n(36, 6, 0.25, 127),
			n(36, 7, 0.25, 127),
		],
	},
	{
		id: "pattern-melody-twinkle",
		name: "Twinkle Melody",
		category: "melodic",
		description: "Twinkle Twinkle Little Star in C major.",
		measures: 4,
		notes: [
			n(60, 0, 1),
			n(60, 1, 1),
			n(67, 2, 1),
			n(67, 3, 1),
			n(69, 4, 1),
			n(69, 5, 1),
			n(67, 6, 2),
			n(65, 8, 1),
			n(65, 9, 1),
			n(64, 10, 1),
			n(64, 11, 1),
			n(62, 12, 1),
			n(62, 13, 1),
			n(60, 14, 2),
		],
	},
] as const;

/** Load user-saved pattern presets from localStorage. */
export function loadUserPatterns(): readonly PatternPreset[] {
	try {
		const json = localStorage.getItem(USER_PATTERNS_KEY);
		if (!json) return [];
		const parsed = JSON.parse(json) as PatternPreset[];
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
}

/** Save user pattern presets to localStorage. */
export function saveUserPatterns(patterns: readonly PatternPreset[]): void {
	try {
		localStorage.setItem(USER_PATTERNS_KEY, JSON.stringify(patterns));
	} catch {
		// Quota exceeded or private browsing
	}
}

/** Save a new user pattern preset. Returns the updated user patterns list. */
export function saveUserPattern(
	name: string,
	notes: readonly SequencerNote[],
	measures: number,
): readonly PatternPreset[] {
	const existing = loadUserPatterns();
	const preset: PatternPreset = {
		id: `user-pattern-${Date.now()}`,
		name,
		category: "user",
		description: `User pattern: ${notes.length} notes, ${measures} bars.`,
		notes,
		measures,
	};
	const updated = [...existing, preset];
	saveUserPatterns(updated);
	return updated;
}

/** Delete a user pattern preset by ID. Returns the updated list. */
export function deleteUserPattern(id: string): readonly PatternPreset[] {
	const existing = loadUserPatterns();
	const updated = existing.filter((p) => p.id !== id);
	saveUserPatterns(updated);
	return updated;
}

/** Get a pattern preset by ID (checks built-in first, then user). */
export function getPatternById(id: string): PatternPreset | undefined {
	return (
		BUILT_IN_PATTERNS.find((p) => p.id === id) ??
		loadUserPatterns().find((p) => p.id === id)
	);
}
